import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { finalize } from 'rxjs';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { CrmMerchandiser, PageResponse } from '../crm.model';
import { CrmService } from '../crm.service';
import { ToolbarService } from '../../services/toolbar.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';

@Component({
  selector: 'app-crm-merchandisers-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    BreadcrumbModule,
    ButtonModule,
    ConfirmDialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    ToastModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageSearchPanelComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './merchandisers-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MerchandisersListComponent implements OnInit {
  private crmService = inject(CrmService);
  private toolbarService = inject(ToolbarService);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);

  rows = signal<CrmMerchandiser[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  first = signal(0);
  pageRows = signal(10);
  searchText = signal('');
  activeFilter = signal<boolean | null>(null);
  lastUpdated = signal<Date | null>(null);

  activeOptions = [
    { label: 'Active', value: true },
    { label: 'Inactive', value: false }
  ];

  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  breadcrumbItems: MenuItem[] = [
    { label: 'CRM', routerLink: '/crm/dashboard' },
    { label: 'Merchandisers', routerLink: '/crm/merchandisers' }
  ];

  metrics = computed<PageMetric[]>(() => {
    const rows = this.rows();
    return [
      { label: 'Merchandisers', value: this.totalRecords() || rows.length, tone: 'info' },
      { label: 'Active', value: rows.filter(row => row.active !== false).length, tone: 'success' },
      { label: 'Inactive', value: rows.filter(row => row.active === false).length, tone: 'warn' },
      { label: 'Visible rows', value: rows.length }
    ];
  });

  ngOnInit(): void {
    this.loadRows();
  }

  loadRows(event?: TableLazyLoadEvent): void {
    if (event) {
      this.first.set(event.first ?? 0);
      this.pageRows.set(event.rows ?? 10);
    }
    const page = Math.floor(this.first() / this.pageRows());
    this.loading.set(true);
    this.crmService.searchMerchandisers({
      companyId: this.companyId(),
      searchText: this.searchText(),
      active: this.activeFilter(),
      page,
      size: this.pageRows(),
      sortBy: 'createdAt',
      sortDir: 'desc'
    }).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: (response: PageResponse<CrmMerchandiser>) => {
        this.rows.set(response.content || []);
        this.totalRecords.set(response.totalElements || 0);
        this.lastUpdated.set(new Date());
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Merchandisers', detail: 'Unable to load merchandisers.' })
    });
  }

  applySearch(): void {
    this.first.set(0);
    this.loadRows();
  }

  clearSearch(): void {
    this.searchText.set('');
    this.activeFilter.set(null);
    this.first.set(0);
    this.loadRows();
  }

  openNew(): void {
    this.router.navigate(['/crm/merchandisers/new']);
  }

  view(row: CrmMerchandiser): void {
    if (row.id) this.router.navigate(['/crm/merchandisers', row.id]);
  }

  edit(row: CrmMerchandiser): void {
    if (row.id) this.router.navigate(['/crm/merchandisers', row.id, 'edit']);
  }

  delete(row: CrmMerchandiser): void {
    if (!row.id) return;
    this.confirmationService.confirm({
      header: 'Delete Merchandiser',
      message: `Delete <b>${row.name}</b>?`,
      icon: 'pi pi-exclamation-circle',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => this.crmService.deleteMerchandiser(row.id!).subscribe({
        next: () => {
          this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Merchandiser deleted.' });
          this.loadRows();
        },
        error: () => this.messageService.add({ severity: 'error', summary: 'Delete Failed', detail: 'Could not delete merchandiser.' })
      })
    });
  }

  get activeSearchCount(): number {
    return [this.searchText(), this.activeFilter()].filter(value => value !== null && value !== undefined && `${value}`.trim() !== '').length;
  }

  get lastUpdatedText(): string {
    const updated = this.lastUpdated();
    return updated ? `Updated ${updated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Ready';
  }

  private companyId(): number | undefined {
    return this.toolbarService.customer()?.company?.id;
  }
}
